import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { InputNumber } from "antd";
import SlayerTaskSearch from "./SlayerTaskSearch.js";
import MonsterCard from "./MonsterCard.js";
import RequirementCheckboxes from "./RequirementCheckboxes.js";
import useCookie from "../hooks/useCookie.js";
import { debounce } from "../utils.js";

const SlayerTaskSection = ({ slayerMasterId, slayerTasks }) => {
  const [filteredData, setFilteredData] = useState([]);
  const [searchString, setSearchString] = useState("");
  const [slayerLevel, setSlayerLevel] = useCookie("slayerLevel", 99);
  const [combatLevel, setCombatLevel] = useCookie("combatLevel", 126);
  const [questCompletionsByMasterStr, setQuestCompletionsByMasterStr] =
    useCookie("questCompletionsByMaster", JSON.stringify({}));
  const [rewardUnlocksByMasterStr, setRewardUnlocksByMasterStr] = useCookie(
    "rewardUnlocksByMaster",
    JSON.stringify({})
  );
  const [blocksByMasterStr, setBlocksByMasterStr] = useCookie(
    "blockedTasksByMaster",
    JSON.stringify({})
  );

  const questCompletionsByMaster = useMemo(() => {
    try {
      return JSON.parse(questCompletionsByMasterStr);
    } catch {
      return {};
    }
  }, [questCompletionsByMasterStr]);
  const rewardUnlocksByMaster = useMemo(() => {
    try {
      return JSON.parse(rewardUnlocksByMasterStr);
    } catch {
      return {};
    }
  }, [rewardUnlocksByMasterStr]);
  const blocksByMaster = useMemo(() => {
    try {
      return JSON.parse(blocksByMasterStr);
    } catch {
      return {};
    }
  }, [blocksByMasterStr]);

  const uniqueQuests = useMemo(() => {
    return Array.from(
      new Set(
        slayerTasks
          .flatMap((task) =>
            task.unlock_requirement.filter((req) => req.type === "quest")
          )
          .map((req) => req.name)
      )
    );
  }, [slayerTasks]);
  const uniqueRewards = useMemo(() => {
    return Array.from(
      new Set(
        slayerTasks
          .filter((task) =>
            task.unlock_requirement.some((req) => req.type === "slayer_reward")
          )
          .map((task) => task.monster_name.name)
      )
    );
  }, [slayerTasks]);
  const uniqueMonsters = useMemo(() => {
    return Array.from(
      new Set(slayerTasks.map((task) => task.monster_name.name))
    ).sort();
  }, [slayerTasks]);

  const completedQuests = useMemo(() => {
    return questCompletionsByMaster[slayerMasterId] || [];
  }, [questCompletionsByMaster, slayerMasterId]);
  const unlockedRewards = useMemo(() => {
    return rewardUnlocksByMaster[slayerMasterId] || [];
  }, [rewardUnlocksByMaster, slayerMasterId]);
  const blockedTasks = useMemo(() => {
    return blocksByMaster[slayerMasterId] || [];
  }, [blocksByMaster, slayerMasterId]);

  useEffect(() => {
    const filtered = slayerTasks.filter((task) => {
      const meetsSearchReq =
        searchString === "" ||
        task.monster_name.name
          .toLowerCase()
          .includes(searchString.toLowerCase());

      const levelReq = task.unlock_requirement.find(
        (req) => req.type === "skill" && req.name === "Slayer"
      );
      const meetsLevelReq =
        !levelReq || parseInt(levelReq.level) <= slayerLevel;

      const combatLevelReq = task.unlock_requirement.find(
        (req) => req.type === "skill" && req.name === "Combat level"
      );
      const meetsCombatLevelReq =
        !combatLevelReq ||
        parseInt(combatLevelReq.level.replace(/\D/g, "")) <= combatLevel;

      const questReqs = task.unlock_requirement.filter(
        (req) => req.type === "quest"
      );
      const meetsQuestReqs =
        questReqs.length === 0 ||
        questReqs.every((req) => completedQuests.includes(req.name));

      const hasRewardReqs = task.unlock_requirement.some(
        (req) => req.type === "slayer_reward"
      );
      const meetsRewardReqs =
        !hasRewardReqs || unlockedRewards.includes(task.monster_name.name);

      const isNotBlockedTask = !blockedTasks.includes(task.monster_name.name);

      return (
        meetsSearchReq &&
        meetsLevelReq &&
        meetsCombatLevelReq &&
        meetsQuestReqs &&
        meetsRewardReqs &&
        isNotBlockedTask
      );
    });

    setFilteredData(filtered);
  }, [
    slayerMasterId,
    searchString,
    slayerLevel,
    combatLevel,
    completedQuests,
    unlockedRewards,
    blockedTasks,
  ]);

  const debouncedSetSlayerLevel = debounce((value) => {
    setSlayerLevel(value || 1);
  }, 300);

  const debouncedSetCombatLevel = debounce((value) => {
    setCombatLevel(value || 1);
  }, 300);

  const handleQuestChange = (questName, checked) => {
    const updatedCompletions = {
      ...questCompletionsByMaster,
      [slayerMasterId]: checked
        ? [...(questCompletionsByMaster[slayerMasterId] || []), questName]
        : (questCompletionsByMaster[slayerMasterId] || []).filter(
            (q) => q !== questName
          ),
    };
    setQuestCompletionsByMasterStr(JSON.stringify(updatedCompletions));
  };

  const handleRewardChange = (rewardName, checked) => {
    const updatedUnlocks = {
      ...rewardUnlocksByMaster,
      [slayerMasterId]: checked
        ? [...(rewardUnlocksByMaster[slayerMasterId] || []), rewardName]
        : (rewardUnlocksByMaster[slayerMasterId] || []).filter(
            (r) => r !== rewardName
          ),
    };
    setRewardUnlocksByMasterStr(JSON.stringify(updatedUnlocks));
  };

  const handleBlocksChange = (monsterName, checked) => {
    const updatedBlocked = {
      ...blocksByMaster,
      [slayerMasterId]: checked
        ? [...(blocksByMaster[slayerMasterId] || []), monsterName]
        : (blocksByMaster[slayerMasterId] || []).filter(
            (m) => m !== monsterName
          ),
    };
    setBlocksByMasterStr(JSON.stringify(updatedBlocked));
  };

  const questItems = uniqueQuests.map((quest) => ({
    id: quest,
    label: quest,
  }));
  const rewardItems = uniqueRewards.map((reward) => ({
    id: reward,
    label: reward,
  }));
  const blockItems = uniqueMonsters.map((monster) => ({
    id: monster,
    label: monster,
  }));

  return (
    <div>
      <SlayerTaskFilters>
        <div>
          Slayer Level:{" "}
          <InputNumber
            min={1}
            max={99}
            defaultValue={slayerLevel}
            onChange={debouncedSetSlayerLevel}
            style={{ marginBottom: "10px" }}
          />
        </div>
        <div>
          Combat Level:{" "}
          <InputNumber
            min={1}
            max={126}
            defaultValue={combatLevel}
            onChange={debouncedSetCombatLevel}
            style={{ marginBottom: "14px" }}
          />
        </div>
        <RequirementCheckboxes
          items={questItems}
          completedItems={completedQuests}
          onChange={handleQuestChange}
          title="Quest Requirements"
          completedText="completed"
        />
        <RequirementCheckboxes
          items={rewardItems}
          completedItems={unlockedRewards}
          onChange={handleRewardChange}
          title="Slayer Point Unlocks"
          completedText="unlocked"
        />
        <RequirementCheckboxes
          items={blockItems}
          completedItems={blockedTasks}
          onChange={handleBlocksChange}
          title="Task Block List"
          completedText="blocked"
        />
      </SlayerTaskFilters>
      <SlayerTaskSearch
        {...{
          slayerTasks: filteredData,
          onSelect: setSearchString,
          onSearch: setSearchString,
        }}
      />
      {filteredData.map((monster, idx) => (
        <MonsterCard key={idx} monster={monster} />
      ))}
    </div>
  );
};

export default SlayerTaskSection;

const SlayerTaskFilters = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
`;
